/**
 * Step to log into an existing account for Clever Library users.
 */
import {t} from "@lingui/core/macro";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingMedium} from "@khanacademy/wonder-blocks-typography";
import {useGql, useQuery} from "@khan/data-gql";
import {useLocation} from "@khan/urls";

import {CONFIRM_ACCOUNT_LINK_USER_QUERY} from "./confirm-account-link-user-query.ts";
import {
    completePostLogin,
    linkClever,
    logoutCleverUser,
} from "./login-actions.ts";
import U13ErrorStep from "./u13-error-step.tsx";

interface Props {
    abandonLoginStep: string;
    onAbandonLogin: () => void;
    onAccountLink: () => void;
    transferToken: string | null | undefined;
    /**
     * Optional identifier to use for the dialog title.
     *
     * Useful for announcing the dialog to Screen Readers, as it will be used
     * as the `aria-labelledby` attribute of the dialog.
     */
    titleId?: string;
}

export default function StudentConfirmAccountLinkStep({
    abandonLoginStep,
    onAbandonLogin,
    onAccountLink,
    transferToken,
    titleId,
}: Props) {
    const gqlFetch = useGql();
    const location = useLocation();
    const [submitting, setSubmitting] = React.useState(false);
    const [loggingOut, setLoggingOut] = React.useState(false);

    const [userQueryResult] = useQuery(CONFIRM_ACCOUNT_LINK_USER_QUERY, {});
    const userData =
        userQueryResult.status === "success"
            ? userQueryResult.data?.user
            : null;

    const completeAccountLink = () => {
        setSubmitting(true);
        onAccountLink();
        if (transferToken) {
            return linkClever(gqlFetch, {transferToken}).then(() => {
                completePostLogin(
                    gqlFetch,
                    false,
                    "/",
                    {isFirstLogin: false},
                    location,
                );
            });
        }
        completePostLogin(gqlFetch, false, "/");
    };

    const abandonLogin = () => {
        setLoggingOut(true);
        onAbandonLogin();
        logoutCleverUser(location, abandonLoginStep);
    };

    const renderConfirmation = () => (
        <View>
            <Strut size={spacing.medium_16} />
            <HeadingMedium id={titleId}>
                {t`Can we use your existing account?`}
            </HeadingMedium>
            <Strut size={spacing.large_24} />
            <Body>
                {t`By using your existing account and adding your Clever ID to it, you will see all your existing progress in this one school account. And it will allow your teachers to see all your activity.`}
            </Body>
            <Strut size={spacing.large_24} />
            <Button
                onClick={completeAccountLink}
                disabled={submitting || loggingOut}
                spinner={submitting}
            >
                {t`Yes, use my existing account`}
            </Button>
            <Strut size={spacing.small_12} />
            <Button
                kind="secondary"
                onClick={abandonLogin}
                disabled={submitting}
                spinner={loggingOut}
            >
                {t`No, create a new account`}
            </Button>
        </View>
    );

    // Show loading spinner while fetching user data
    if (userQueryResult.status === "loading") {
        return (
            <View style={{alignItems: "center"}}>
                <CircularSpinner />
            </View>
        );
    }

    // If there's an error or no user data, show error step
    if (userQueryResult.status === "error" || !userData) {
        return (
            <U13ErrorStep
                titleId={titleId}
                nickname="User"
                ageMissing={true}
                onSignUpClick={abandonLogin}
            />
        );
    }

    const isDistrictUser =
        !!userData?.cleverId || !!userData?.userDistrictInfos?.length;

    const age = userData?.age;
    const nickname = userData?.nickname;

    if (isDistrictUser) {
        return renderConfirmation();
    }

    if (!age || !nickname) {
        return (
            <U13ErrorStep
                titleId={titleId}
                nickname="User"
                ageMissing={true}
                onSignUpClick={abandonLogin}
            />
        );
    }

    if (age < 13) {
        return (
            <U13ErrorStep
                titleId={titleId}
                nickname={nickname}
                ageMissing={false}
                onSignUpClick={abandonLogin}
            />
        );
    }
    return renderConfirmation();
}
